import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RotateCcw, Trophy } from 'lucide-react';

const FILES = 'abcdefgh';
const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// Build a FEN containing ONLY the student's chess piece — obstacles/targets are
// rendered as emoji overlays via extraSquareStyles. Ghost-king injection was
// removed after the legalMoves recomputation stopped depending on `selected`;
// chess.js no longer needs kings present to accept the drops we produce.
function piecesToFen(pieces, turn = 'w') {
  const map = {};
  (pieces || []).forEach(p => { if (p.piece && p.color) map[p.square] = p; });
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const sq = FILES[f] + r;
      const cell = map[sq];
      if (cell) {
        if (empty > 0) { row += empty; empty = 0; }
        const p = cell.piece.toLowerCase();
        row += cell.color === 'w' ? p.toUpperCase() : p;
      } else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return ranks.join('/') + ` ${turn} - - 0 1`;
}

function emojiToBg(emoji) {
  const safe = String(emoji).replace(/"/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text x='50' y='55' text-anchor='middle' dominant-baseline='central' font-size='68'>${safe}</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

// Rook: rays in the 4 orthogonal directions. Obstacles block; targets do not
// (they get captured when the rook LANDS on them).
function computeRookMoves(from, obstacleSquares) {
  const file = FILES.indexOf(from[0]);
  const rank = parseInt(from[1], 10) - 1;
  const obstacles = new Set(obstacleSquares || []);
  const out = new Set();
  const rays = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  for (const [df, dr] of rays) {
    let f = file + df, r = rank + dr;
    while (f >= 0 && f < 8 && r >= 0 && r < 8) {
      const sq = FILES[f] + (r + 1);
      if (obstacles.has(sq)) break;
      out.add(sq);
      f += df; r += dr;
    }
  }
  return out;
}

// Bishop: 4 diagonal rays with the same obstacle-blocking semantics.
function computeBishopMoves(from, obstacleSquares) {
  const file = FILES.indexOf(from[0]);
  const rank = parseInt(from[1], 10) - 1;
  const obstacles = new Set(obstacleSquares || []);
  const out = new Set();
  const rays = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
  for (const [df, dr] of rays) {
    let f = file + df, r = rank + dr;
    while (f >= 0 && f < 8 && r >= 0 && r < 8) {
      const sq = FILES[f] + (r + 1);
      if (obstacles.has(sq)) break;
      out.add(sq);
      f += df; r += dr;
    }
  }
  return out;
}

// Knight: 8 L-shaped jumps. Obstacles between origin and destination are
// ignored (the knight jumps), but the knight cannot LAND on an obstacle.
function computeKnightMoves(from, obstacleSquares) {
  const file = FILES.indexOf(from[0]);
  const rank = parseInt(from[1], 10) - 1;
  const obstacles = new Set(obstacleSquares || []);
  const offsets = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];
  const out = new Set();
  for (const [df, dr] of offsets) {
    const f = file + df, r = rank + dr;
    if (f < 0 || f >= 8 || r < 0 || r >= 8) continue;
    const sq = FILES[f] + (r + 1);
    if (obstacles.has(sq)) continue;
    out.add(sq);
  }
  return out;
}

// Queen: rook ∪ bishop rays. Same ray-blocking semantics.
function computeQueenMoves(from, obstacleSquares) {
  const out = new Set();
  computeRookMoves(from, obstacleSquares).forEach(sq => out.add(sq));
  computeBishopMoves(from, obstacleSquares).forEach(sq => out.add(sq));
  return out;
}

// King: exactly one square in any of the 8 directions; obstacles block.
function computeKingMoves(from, obstacleSquares) {
  const file = FILES.indexOf(from[0]);
  const rank = parseInt(from[1], 10) - 1;
  const obstacles = new Set(obstacleSquares || []);
  const offsets = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
  const out = new Set();
  for (const [df, dr] of offsets) {
    const f = file + df, r = rank + dr;
    if (f < 0 || f >= 8 || r < 0 || r >= 8) continue;
    const sq = FILES[f] + (r + 1);
    if (obstacles.has(sq)) continue;
    out.add(sq);
  }
  return out;
}

// Pawn: color-aware. Advances forward 1 square if empty; 2 squares from the
// starting rank if both are empty. Captures diagonally forward ONLY when the
// diagonal-forward square holds a target. Cannot capture straight forward.
function computePawnMoves(from, color, obstacleSquares, targetSquares) {
  const file = FILES.indexOf(from[0]);
  const rank = parseInt(from[1], 10) - 1;
  const dir = color === 'w' ? 1 : -1;
  const startRank = color === 'w' ? 1 : 6;
  const obstacles = new Set(obstacleSquares || []);
  const targets = new Set(targetSquares || []);
  const blockers = new Set([...obstacles, ...targets]);
  const out = new Set();
  const r1 = rank + dir;
  if (r1 >= 0 && r1 < 8) {
    const s1 = FILES[file] + (r1 + 1);
    if (!blockers.has(s1)) {
      out.add(s1);
      if (rank === startRank) {
        const r2 = rank + 2 * dir;
        if (r2 >= 0 && r2 < 8) {
          const s2 = FILES[file] + (r2 + 1);
          if (!blockers.has(s2)) out.add(s2);
        }
      }
    }
  }
  for (const df of [-1, 1]) {
    const f = file + df;
    if (f < 0 || f >= 8) continue;
    const r = rank + dir;
    if (r < 0 || r >= 8) continue;
    const sq = FILES[f] + (r + 1);
    if (targets.has(sq)) out.add(sq);
  }
  return out;
}

function computeLegalMoves(pieceCode, from, obstacleSquares, color, targetSquares) {
  if (pieceCode === 'R') return computeRookMoves(from, obstacleSquares);
  if (pieceCode === 'B') return computeBishopMoves(from, obstacleSquares);
  if (pieceCode === 'N') return computeKnightMoves(from, obstacleSquares);
  if (pieceCode === 'Q') return computeQueenMoves(from, obstacleSquares);
  if (pieceCode === 'K') return computeKingMoves(from, obstacleSquares);
  if (pieceCode === 'P') return computePawnMoves(from, color, obstacleSquares, targetSquares);
  return new Set();
}

// Sliding pieces cannot end their move BEYOND a target on the ray. Returns
// true when a target sits STRICTLY between `from` and `to` (excluding both).
// Rook/Bishop/Queen only — knight jumps and king moves have no traversable
// intermediate squares.
function pathHasBlockingTarget(from, to, targetSquares) {
  const fx = FILES.indexOf(from[0]);
  const fy = parseInt(from[1], 10) - 1;
  const tx = FILES.indexOf(to[0]);
  const ty = parseInt(to[1], 10) - 1;
  const dx = tx - fx, dy = ty - fy;
  const isOrtho = (dx === 0) !== (dy === 0);
  const isDiag = Math.abs(dx) === Math.abs(dy) && dx !== 0;
  if (!isOrtho && !isDiag) return false;
  const sx = Math.sign(dx), sy = Math.sign(dy);
  const steps = Math.max(Math.abs(dx), Math.abs(dy));
  const targets = new Set(targetSquares || []);
  for (let i = 1; i < steps; i++) {
    const x = fx + sx * i, y = fy + sy * i;
    const sq = FILES[x] + (y + 1);
    if (targets.has(sq)) return true;
  }
  return false;
}

import { playVictoryOnce } from '@/lib/victorySound';
import { playChessMoveSound, playCaptureActivityCollectSound, playCaptureActivityFinalCollectSound } from '@/lib/chessSounds';

export default function CaptureActivityPlayer({ open, onClose, activity, onProgress, inline = false }) {
  const cfg = activity?.capture_config || null;
  const initialPieces = useMemo(() => (
    Array.isArray(cfg?.pieces) ? cfg.pieces.map(p => ({ ...p })) : []
  ), [cfg]);

  const [pieces, setPieces] = useState(initialPieces);
  const [selected, setSelected] = useState(null);
  const [flash, setFlash] = useState(null); // { square, ts }
  const [won, setWon] = useState(false);
  const [moves, setMoves] = useState(0);
  // Timestamp of when the activity started so we can show "Tiempo utilizado
  // XX:XX" on the celebration screen. Purely local + purely visual — does not
  // touch the existing timer/countdown logic or the teacher's monitor.
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  // Load / reset on open
  useEffect(() => {
    if (!open) return;
    setPieces(initialPieces);
    setSelected(null); setFlash(null); setWon(false); setMoves(0);
    setStartedAt(Date.now()); setElapsedSec(0);
  }, [open, initialPieces]);

  // Prevent iOS "scroll-focused-element-into-view" jump after a capture.
  // KEY INSIGHT (from testing_agent iteration_25): the browser applies the
  // scroll SYNCHRONOUSLY BEFORE dispatching focusin, so reading scrollTop
  // inside focusin already captures the scrolled value. We must instead
  // track the last user-driven scroll position CONTINUOUSLY (updated only
  // by real user gestures) and revert any scroll that happens within a
  // small window after a focus event.
  useEffect(() => {
    if (!inline) return;
    const scroller = document.querySelector('[data-testid=student-exercise-view]');
    const player = document.querySelector('[data-testid=capture-player]');
    if (!scroller || !player) return;
    let userTop = scroller.scrollTop;
    let userWinY = window.scrollY;
    let userGestureAt = 0;
    let focusArmedAt = 0;
    const now = () => Date.now();
    const markGesture = () => {
      userGestureAt = now();
      userTop = scroller.scrollTop;
      userWinY = window.scrollY;
    };
    const onFocusIn = () => { focusArmedAt = now(); };
    const onScroll = () => {
      const t = now();
      // Within 400ms of a user gesture: accept the new position as user-driven.
      if (t - userGestureAt < 400) {
        userTop = scroller.scrollTop;
        userWinY = window.scrollY;
        return;
      }
      // Within 400ms of a focus event with no gesture: revert (focus-into-view).
      if (focusArmedAt && t - focusArmedAt < 400) {
        if (scroller.scrollTop !== userTop) scroller.scrollTop = userTop;
        if (window.scrollY !== userWinY) window.scrollTo(0, userWinY);
      } else {
        // Programmatic scroll that isn't a focus jump: accept it.
        userTop = scroller.scrollTop;
        userWinY = window.scrollY;
      }
    };
    scroller.addEventListener('wheel',      markGesture, { passive: true, capture: true });
    scroller.addEventListener('touchstart', markGesture, { passive: true, capture: true });
    scroller.addEventListener('touchmove',  markGesture, { passive: true, capture: true });
    scroller.addEventListener('pointerdown', markGesture, { passive: true, capture: true });
    scroller.addEventListener('mousedown',   markGesture, { passive: true, capture: true });
    scroller.addEventListener('keydown',     markGesture, { passive: true, capture: true });
    player.addEventListener('focusin', onFocusIn, true);
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('wheel', markGesture, true);
      scroller.removeEventListener('touchstart', markGesture, true);
      scroller.removeEventListener('touchmove', markGesture, true);
      scroller.removeEventListener('pointerdown', markGesture, true);
      scroller.removeEventListener('mousedown', markGesture, true);
      scroller.removeEventListener('keydown', markGesture, true);
      player.removeEventListener('focusin', onFocusIn, true);
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [inline, open]);

  // Elapsed-time ticker (stops the moment the student wins so the final time
  // stays frozen on the celebration card).
  useEffect(() => {
    if (!open || won || !startedAt) return;
    const iv = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 500);
    return () => clearInterval(iv);
  }, [open, won, startedAt]);

  const student = useMemo(() => pieces.find(p => p.role === 'student'), [pieces]);
  const targets = useMemo(() => pieces.filter(p => p.role === 'target'), [pieces]);
  const obstacles = useMemo(() => pieces.filter(p => p.role === 'obstacle'), [pieces]);

  const legalMoves = useMemo(() => {
    // Compute always (independent of `selected`) so drag & drop — which never
    // goes through the click-to-select step — can validate destinations too.
    // `selected` still drives the yellow highlight in extraSquareStyles below;
    // this useMemo just stops gating the legal set on it.
    if (!student) return new Set();
    return computeLegalMoves(
      student.piece,
      student.square,
      obstacles.map(o => o.square),
      student.color,
      targets.map(t => t.square),
    );
  }, [student, obstacles, targets]);

  // Win detection
  useEffect(() => {
    if (!open) return;
    if (targets.length === 0 && initialPieces.some(p => p.role === 'target')) {
      setWon(true);
    }
  }, [targets, open, initialPieces]);

  // Time-out detection. When the activity carries a `duration_seconds` and
  // `started_at` (set by the teacher at ASK time), tick every 500ms and flip
  // `timeExpired` to true when the clock hits zero. Purely local; the teacher
  // already has the authoritative countdown in TeacherCaptureLiveView.
  const [timeExpired, setTimeExpired] = useState(false);
  useEffect(() => {
    if (!open || won) return;
    if (!activity?.duration_seconds || !activity?.started_at) return;
    const started = new Date(activity.started_at).getTime();
    const dur = activity.duration_seconds * 1000;
    const tick = () => {
      if (Date.now() - started >= dur) setTimeExpired(true);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [open, won, activity?.duration_seconds, activity?.started_at]);

  // Points — proportional to captured targets out of 30 max. Rounded to nearest
  // integer per spec (e.g. 3/4 → 23, 1/4 → 8). Applied identically whether the
  // student wins early (captured == total → 30) or the time runs out with a
  // partial capture.
  const totalTargetsForPoints = useMemo(
    () => initialPieces.filter(p => p.role === 'target').length,
    [initialPieces]
  );
  const capturedNow = totalTargetsForPoints - targets.length;
  const points = totalTargetsForPoints > 0
    ? Math.round((capturedNow / totalTargetsForPoints) * 30)
    : 0;

  // Auto-close after individual completion.
  //
  // Requirement: once the student captures the last target we must (1) show
  // the "¡EJERCICIO COMPLETADO!" celebration briefly, (2) let the fire-and-
  // forget `onProgress({completed:true})` — dispatched INSIDE tryMove BEFORE
  // `won` flips — reach the server so the teacher's monitor sees COMPLETADO,
  // and (3) automatically dismiss THIS student's modal so they return to the
  // Classroom without having to click "Cerrar".
  //
  // This close is purely LOCAL: it calls the parent's `onClose` which just
  // flips the bridge's `dismissed` flag. It never hits `/puzzle/end`, so the
  // classroom's global activity stays active for the other students AND for
  // the teacher's monitor. The teacher still owns the lifecycle via the
  // "Terminar ejercicio" button.
  // Auto-close after individual completion — ONLY in modal (Studies preview)
  // mode. When rendered INLINE inside the Classroom "Ejercicios" area we must
  // NOT auto-close: the completed activity has to remain visible with the
  // final board + "¡EJERCICIO COMPLETADO!" banner and stay there until the
  // TEACHER pulses "Cerrar ejercicio" (which sets active_puzzle:None and
  // unmounts LivePuzzle for everyone).
  useEffect(() => {
    if (inline) return;
    if (!open || !won) return;
    const t = setTimeout(() => { onClose?.(); }, 1800);
    return () => clearTimeout(t);
  }, [inline, open, won, onClose]);

  const tryMove = useCallback((from, to) => {
    if (won || timeExpired || !student) return false;
    if (from !== student.square) return false;
    // Sliding pieces cannot end past a target on the ray — reject completely.
    if (['R', 'B', 'Q'].includes(student.piece)) {
      if (pathHasBlockingTarget(from, to, targets.map(t => t.square))) {
        setFlash({ square: to, ts: Date.now() });
        setTimeout(() => setFlash(f => (f && f.square === to ? null : f)), 600);
        return false;
      }
    }
    if (!legalMoves.has(to)) {
      setFlash({ square: to, ts: Date.now() });
      setTimeout(() => setFlash(f => (f && f.square === to ? null : f)), 600);
      return false;
    }
    // Compute the next pieces snapshot OUTSIDE the state updater so onProgress
    // (HTTP POST) is never invoked inside `setPieces(prev => ...)`. That was
    // an antipattern: React can re-run updaters (StrictMode dev, concurrent
    // features) which duplicated the network call. The updater below is now
    // a pure function returning the new pieces array.
    const next = pieces
      .filter(p => !(p.role === 'target' && p.square === to))
      .map(p => (p.role === 'student' ? { ...p, square: to } : p));
    // Detect capture vs plain slide using the SAME source-of-truth the state
    // update just committed. suppressSounds on ChessBoardView guarantees the
    // generic chess move/capture cues never fire here, so every audio event
    // for a Capture Activity is produced by this branch — no duplicates.
    const wasCollect = pieces.some(p => p.role === 'target' && p.square === to);
    const totalNow = initialPieces.filter(p => p.role === 'target').length;
    const remainingAfter = next.filter(p => p.role === 'target').length;
    if (wasCollect) {
      if (remainingAfter === 0 && totalNow > 0) {
        playCaptureActivityFinalCollectSound();
      } else {
        playCaptureActivityCollectSound();
      }
    } else {
      playChessMoveSound();
    }
    setPieces(next);
    setSelected(null);
    setMoves(m => m + 1);
    // Fire real-time progress AFTER the state updates. Values are computed
    // from `next` (the exact snapshot we just committed) so numbers match
    // the board the teacher will see. Errors are swallowed to avoid
    // surfacing transport failures to the student.
    if (onProgress) {
      const total = initialPieces.filter(p => p.role === 'target').length;
      const remaining = next.filter(p => p.role === 'target').length;
      try {
        onProgress({
          captured: total - remaining,
          total,
          moves_done: moves + 1,
          completed: remaining === 0 && total > 0,
          pieces: next,
        });
      } catch { /* ignore transport errors */ }
      // Play the victory chime the FIRST time this student captures the
      // last target. Dedup key is per-activity so replays / re-renders /
      // teacher-side onProgress hooks never re-trigger the sound.
      if (remaining === 0 && total > 0) {
        playVictoryOnce(`capture:${activity?.id || activity?.title || 'na'}`);
      }
    }
    return true;
  }, [won, timeExpired, student, legalMoves, targets, pieces, onProgress, initialPieces, moves, activity]);

  const handleSquareClick = useCallback((square) => {
    if (won || !student) return;
    if (square === student.square) {
      setSelected(prev => (prev === student.square ? null : student.square));
      return;
    }
    if (selected === student.square) tryMove(student.square, square);
  }, [won, student, selected, tryMove]);

  const handleDrop = useCallback((from, to) => tryMove(from, to), [tryMove]);

  const fen = useMemo(
    () => piecesToFen(pieces, student?.color || 'w'),
    [pieces, student]
  );

  const extraSquareStyles = useMemo(() => {
    const styles = {};
    // Draw obstacles (blocker tint + emoji)
    obstacles.forEach(o => {
      styles[o.square] = {
        backgroundColor: 'rgba(100, 116, 139, 0.35)',
        backgroundImage: emojiToBg(o.emoji || '🧱'),
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '78% 78%',
        transition: 'background-color 150ms ease',
      };
    });
    // Draw targets (emoji only — no red tint). The square gets the target
    // emoji as a background image; the red highlight was purely a visual
    // hint and has been removed per the "clean board" spec. Capture logic
    // is unaffected: tryMove / legalMoves still see this square as a target.
    targets.forEach(t => {
      styles[t.square] = {
        backgroundImage: emojiToBg(t.emoji || '🎯'),
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '78% 78%',
      };
    });
    // Selected square (student) — yellow highlight
    if (student && selected === student.square) {
      styles[student.square] = { ...(styles[student.square] || {}), backgroundColor: 'rgba(234, 179, 8, 0.55)' };
    }
    // Legal move markers — Lichess-style. Small dot for an empty legal square;
    // large semi-transparent ring for a legal capture (target square). Only
    // shown VISUALLY when the student has clicked the piece; `legalMoves` is
    // still computed unconditionally so drag & drop works without a prior
    // click.
    if (student && selected === student.square) {
      legalMoves.forEach(sq => {
        const isCapture = targets.some(t => t.square === sq);
        // Empty square → small centered dot (unchanged).
        // Capture square → crisp defined ring hugging the square edges,
        // sitting AROUND the object (which is drawn at ~78% size). Sharp
        // radial-gradient stops (88%→96%) give a well-defined circle
        // instead of a diffuse halo. Fully transparent inside so the
        // object stays completely visible.
        const marker = isCapture
          ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
          : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)';
        const existing = styles[sq]?.backgroundImage;
        styles[sq] = existing ? {
          ...(styles[sq] || {}),
          backgroundImage: `${marker}, ${existing}`,
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'center, center',
          backgroundSize: '100% 100%, 78% 78%',
        } : {
          ...(styles[sq] || {}),
          backgroundImage: marker,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '100% 100%',
        };
      });
    }
    // Invalid-move flash
    if (flash?.square) {
      styles[flash.square] = { ...(styles[flash.square] || {}), backgroundColor: 'rgba(239, 68, 68, 0.55)' };
    }
    return styles;
  }, [obstacles, targets, student, selected, legalMoves, flash]);

  const totalTargets = initialPieces.filter(p => p.role === 'target').length;
  const captured = totalTargets - targets.length;
  const supported = student && ['R', 'B', 'N', 'Q', 'K', 'P'].includes(student.piece);

  // Responsive board size — on mobile the fixed 480px board would overflow
  // horizontally (e.g. iPhone 375px viewport). Recompute on resize so the
  // board never exceeds its container. Desktop keeps the 480px experience.
  const boardWrapRef = useRef(null);
  const [boardPx, setBoardPx] = useState(480);
  useEffect(() => {
    // Layout-only: mide el contenedor de la cuadrícula (ResizeObserver). En
    // móvil (1 columna) usa el ancho completo; en >=768px (tablero + panel en
    // 2 columnas) reserva espacio para el panel para no desbordar cuando el
    // sidebar reduce el ancho útil. Tope 480, mínimo 240.
    const el = boardWrapRef.current;
    const compute = () => {
      const cw = el && el.clientWidth ? el.clientWidth : (typeof window !== 'undefined' ? window.innerWidth - 48 : 480);
      const twoCol = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;
      const widthAvail = inline ? cw : (twoCol ? Math.floor(cw * 0.56) - 24 : cw);
      const top = el && typeof window !== 'undefined' ? el.getBoundingClientRect().top : 0;
      const heightAvail = typeof window !== 'undefined' ? window.innerHeight - top - 24 : 480;
      const avail = Math.min(widthAvail, heightAvail);
      setBoardPx(Math.min(480, Math.max(160, Math.floor(avail))));
    };
    compute();
    let ro;
    if (el && typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(compute); ro.observe(el); }
    window.addEventListener('resize', compute);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', compute); };
  }, [inline, open]);

  const resetGame = () => {
    setPieces(initialPieces);
    setSelected(null); setFlash(null); setWon(false); setMoves(0);
  };

  // Kid-friendly motivational message shown while the student still has
  // targets pending. Rotates deterministically based on the moves count so it
  // feels alive but doesn't shuffle every render. Purely visual: never blocks
  // or penalises the student.
  const remaining = targets.length;
  const MOTIVATIONAL = [
    { icon: '🔎', title: '¡Sigue buscando!',           body: 'Todavía quedan objetos por capturar.' },
    { icon: '👀', title: '¡Buen intento!',             body: 'Busca tu siguiente objetivo. 🎯' },
    { icon: '🚀', title: '¡Sigue adelante!',           body: '¡Todavía quedan algunos objetos!' },
    { icon: '🧠', title: '¡Piensa tu siguiente movimiento!', body: '¡Tú puedes completar la misión! 💪' },
    { icon: '🎯', title: '¡Ya casi!',                  body: `Solo quedan ${remaining} objetos. ¡A atraparlos! 😎` },
    { icon: '🔥', title: '¡Vas muy bien!',             body: '¡No te detengas, todavía puedes lograrlo!' },
  ];
  const motivational = MOTIVATIONAL[moves % MOTIVATIONAL.length];

  const emm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ess = String(elapsedSec % 60).padStart(2, '0');

  const body = (
    <>
      <div ref={boardWrapRef} className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
        <div className={`grid relative w-full max-w-full ${inline ? "place-items-start justify-items-center translate-y-[20px]" : "place-items-center"}`}>
          <ChessBoardView
            fen={fen || START}
            boardWidth={boardPx}
            arePiecesDraggable={!won && !timeExpired && !!supported}
            onSquareClick={handleSquareClick}
            onPieceDrop={handleDrop}
            extraSquareStyles={extraSquareStyles}
            suppressSounds
          />
          {won && (
            <div data-testid="capture-win-overlay" className="absolute inset-0 grid place-items-center pointer-events-none animate-in fade-in zoom-in duration-300">
              <div className="bg-gradient-to-br from-emerald-500 via-emerald-400 to-teal-500 text-white px-7 py-5 rounded-3xl shadow-2xl border-4 border-white text-center max-w-[380px]">
                <div className="text-2xl font-extrabold leading-tight tracking-wide">🎉 ¡MISIÓN CUMPLIDA! 🎉</div>
                <div className="text-base font-bold opacity-95 mt-1 flex items-center justify-center gap-1"><Trophy className="w-4 h-4"/> ¡EXCELENTE TRABAJO!</div>
                <div className="text-sm opacity-90 mt-2">Capturaste todos los objetos.</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-left">
                  <div className="bg-white/20 rounded-xl px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">🍕 Capturados</div>
                    <div className="text-lg font-extrabold">{captured} / {totalTargets}</div>
                  </div>
                  <div className="bg-white/20 rounded-xl px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest opacity-80">⏱️ Tiempo</div>
                    <div className="text-lg font-extrabold font-mono">{emm}:{ess}</div>
                  </div>
                </div>
                <div data-testid="capture-points" className="mt-3 inline-flex items-center gap-2 bg-white/25 rounded-full px-4 py-1.5 font-extrabold text-lg">
                  ⭐ {points} PUNTOS
                </div>
                <div className="mt-3 text-sm font-semibold opacity-95">¡Lo lograste! Sigue así, cada ejercicio te hace mejor jugador. ♟️🚀</div>
              </div>
            </div>
          )}
          {!won && timeExpired && (
            <div data-testid="capture-timeup-overlay" className="absolute inset-0 grid place-items-center pointer-events-none animate-in fade-in zoom-in duration-300">
              <div className="bg-gradient-to-br from-amber-500 via-orange-400 to-rose-400 text-white px-7 py-5 rounded-3xl shadow-2xl border-4 border-white text-center max-w-[380px]">
                <div className="text-2xl font-extrabold leading-tight tracking-wide">⏰ ¡TIEMPO!</div>
                <div className="text-base font-bold opacity-95 mt-1">
                  {capturedNow === 0
                    ? '🎯 ¡SIGUE ADELANTE!'
                    : (totalTargetsForPoints > 0 && capturedNow / totalTargetsForPoints >= 0.5)
                      ? '🎯 ¡CASI LO LOGRAS!'
                      : '🎯 ¡SIGUE ADELANTE!'}
                </div>
                <div className="text-sm opacity-95 mt-2">Capturaste {capturedNow} de {totalTargetsForPoints} objetos.</div>
                <div data-testid="capture-points" className="mt-3 inline-flex items-center gap-2 bg-white/25 rounded-full px-4 py-1.5 font-extrabold text-lg">
                  ⭐ {points} PUNTOS
                </div>
                <div className="mt-3 text-sm font-semibold opacity-95">💪 ¡Sigue así, pronto lo harás perfecto! 🚀♟️</div>
              </div>
            </div>
          )}
        </div>

        <div className={inline ? "space-y-2.5 translate-y-[28px] max-h-[360px] overflow-y-auto overscroll-contain md:max-h-none md:overflow-visible" : "space-y-3"}>
          {inline && (
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-2.5">
              <div className="text-[13px] font-bold text-slate-800">Tu misión:</div>
              <div className="text-xs text-slate-700 mt-0.5">{cfg?.instructions || activity?.instructions || 'Usa tu pieza para capturar todos los objetos del tablero.'} 🏆</div>
            </div>
          )}

          <div className={inline ? "grid grid-cols-2 gap-3" : "space-y-3"}>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
            <div className="text-[11px] text-slate-500 uppercase tracking-widest mb-0.5">Progreso</div>
            <div className="text-lg font-extrabold" data-testid="capture-progress-count">{captured} / {totalTargets}</div>
            <div className="text-[11px] text-slate-500">objetos capturados</div>
          </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest mb-0.5">Movimientos</div>
              <div className="text-lg font-extrabold" data-testid="capture-moves-count">{moves}</div>
            </div>
          </div>

          {/* Motivational message shown while resolving (no penalties, no
              errors). Swap for the "misión cumplida" banner when won. */}
          {!won ? (
            <div
              data-testid="capture-motivational"
              className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 p-2.5 shadow-sm"
              style={{ minHeight: 60, overflowAnchor: 'none' }}
            >
              <div className="flex items-start gap-2">
                <div className="text-xl leading-none">{motivational.icon}</div>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-amber-800">{motivational.title}</div>
                  <div className="text-[11px] text-amber-700 mt-0.5">{motivational.body}</div>
                </div>
              </div>
            </div>
          ) : (
            inline && (
              <div data-testid="capture-wait-teacher" className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 text-emerald-800 p-3 font-semibold text-sm text-center animate-in fade-in duration-500">
                ¡Terminaste! Espera a que el profesor cierre el ejercicio.
              </div>
            )
          )}

          {/* Recuerda: solo instrucciones motivacionales, sin penalizaciones. */}
          <div className="rounded-2xl border border-slate-200 bg-white p-2.5 text-[11px] text-slate-600 space-y-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">💡 Recuerda</div>
            <div className="flex gap-2"><span>♟️</span><span>Tu pieza solo puede moverse como corresponde.</span></div>
            <div className="flex gap-2"><span>🧱</span><span>Los obstáculos bloquean el camino.</span></div>
            <div className="flex gap-2"><span>🪄</span><span>Los caballos pueden saltar obstáculos.</span></div>
            <div className="flex gap-2"><span>🔄</span><span>Puedes hacer todos los movimientos que necesites.</span></div>
            <div className="flex gap-2"><span>❤️</span><span>¡No importa cuántos movimientos hagas!</span></div>
            <div className="flex gap-2"><span>⏱️</span><span>Lo importante es completar la misión antes de que se acabe el tiempo.</span></div>
            <div className="pt-2 border-t border-slate-100 mt-2 space-y-1 text-slate-500">
              <div>• Haz clic en tu pieza para seleccionarla, luego clic en la casilla destino.</div>
              <div>• También puedes arrastrarla.</div>
              <div>• Los objetos se capturan al aterrizar.</div>
            </div>
            {!supported && student && (
              <div className="mt-2 rounded bg-amber-50 border border-amber-200 text-amber-800 p-2">
                Esta pieza aún no está soportada en el reproductor.
              </div>
            )}
          </div>

          {/* Reiniciar sólo tiene sentido en el modal de Studies. Dentro del
              Classroom la actividad la controla el profesor con "Cerrar
              ejercicio", así que ocultamos el botón para no confundir. */}
          {!inline && (
            <Button data-testid="capture-reset" variant="outline" onClick={resetGame} className="w-full">
              <RotateCcw className="w-4 h-4 mr-2"/> Reiniciar
            </Button>
          )}
        </div>
      </div>
    </>
  );

  // Inline mode: render the activity DIRECTLY inside the Classroom
  // "Ejercicios" area — no Dialog wrapper, no backdrop, no Cerrar button.
  // The teacher's "Cerrar ejercicio" is the ONLY exit path (unmounts the
  // whole LivePuzzle when active_puzzle becomes None).
  if (inline) {
    return (
      <div data-testid="capture-player" className="w-full max-w-full bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 pb-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        {/* Mission-style header: friendly, motivating, kid-appropriate. */}
        <div className="relative -mb-[11px]">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest shadow">
              🎯 ¡Cumple la misión!
            </div>
          </div>
          <div className="hidden md:block mt-2 w-full text-center text-xl font-display font-extrabold text-slate-900 whitespace-normal md:absolute md:left-[calc(50%+155px)] md:top-1/2 md:mt-0 md:w-auto md:-translate-y-1/2 md:text-left md:whitespace-nowrap" data-testid="capture-title">{activity?.title || 'Capture Activity'}</div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="capture-player" className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{activity?.title || 'Capture Activity'}</DialogTitle>
          <DialogDescription>{cfg?.instructions || activity?.instructions || 'Mueve tu pieza y captura todos los objetos.'}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
